import React, { useCallback, useMemo } from 'react';
import { Alert, Text } from '@goodfynd/ui';
import { StyledFlex, StyledFlexBetweenH } from '@goodfynd/styles';
import { useTheme } from '@goodfynd/theme';
import { Skeleton } from '@mui/material';
import { getThemeColor } from 'utils/base';

import { useOrdersOverview } from '../../orders-overview-context/OrdersOverviewProvider';
import { OverviewCard } from '../../overview-card';

import { StyledChart, StyledProductSquare } from './styles';
import type { PopularProductsOverviewProps, ProductData } from './types';

import classes from './PopularProductsOverview.module.css';

export default function PopularProductsOverview({
  ...props
}: PopularProductsOverviewProps) {
  const { isDarkMode } = useTheme();

  const {
    popularProducts,
    popularProductsAreFetching,
    popularProductsHasError,
    totalItems,
  } = useOrdersOverview();

  const colorOptions = useMemo<Record<ProductData['color'], string>>(
    () => ({
      blue: getThemeColor('ComponentTagTextBlue', isDarkMode),
      gray: getThemeColor('TextIconSecondary', isDarkMode),
      green: getThemeColor('TextIconFavorable', isDarkMode),
      lightgreen: getThemeColor('ComponentAvatarIconGreen', isDarkMode),
      orange: getThemeColor('ComponentTagTextOrange', isDarkMode),
      purple: getThemeColor('ComponentTagBackgroundPurple', isDarkMode),
      red: getThemeColor('TextIconDanger', isDarkMode),
    }),
    [isDarkMode]
  );

  const products = useMemo<ProductData[]>(() => {
    const colorDictionary: Array<keyof typeof colorOptions> = [
      'gray',
      'red',
      'lightgreen',
      'purple',
      'orange',
      'blue',
      'green',
    ];

    return popularProducts.map((product) => ({
      ...product,
      color: colorDictionary.pop() || 'gray',
    }));
  }, [popularProducts]);

  const renderPopularProducts = useCallback(() => {
    if (popularProductsHasError) {
      return <Alert status="error">An error has occurred</Alert>;
    }

    return (
      <StyledChart>
        <StyledFlex css={{ flexWrap: 'nowrap', gap: 4, marginBottom: 24 }}>
          {products.map(({ color, name, quantity }) => (
            <StyledProductSquare
              key={name}
              css={{
                backgroundColor: color && colorOptions[color],
                flexBasis: `${((quantity * 100) / totalItems).toFixed(3)}%`,
                margin: 0,
              }}
            />
          ))}
        </StyledFlex>

        {products[0] ? (
          products.map(({ color, name, quantity }) => (
            <StyledFlexBetweenH
              css={{ alignItems: 'center', gap: 8, marginTop: 12 }}
              key={name}
            >
              <StyledFlex
                css={{
                  alignItems: 'center',
                  flex: '2 1 auto',
                  flexWrap: 'nowrap',
                }}
              >
                <StyledProductSquare
                  css={{
                    backgroundColor: color && colorOptions[color],
                  }}
                />

                <Text as="body2" className={classes.name} truncate={1}>
                  {name}
                </Text>
              </StyledFlex>

              {quantity > 0 && <Text as="body2">{quantity} sold</Text>}
            </StyledFlexBetweenH>
          ))
        ) : (
          <Text align="center">Not enough data to display</Text>
        )}
      </StyledChart>
    );
  }, [colorOptions, popularProductsHasError, products, totalItems]);

  if (!totalItems) {
    return null;
  }

  return (
    <OverviewCard
      {...props}
      id="overview-popular-products"
      title="Popular products"
    >
      {popularProductsAreFetching ? (
        <>
          <StyledFlex css={{ flexWrap: 'nowrap', gap: 4, marginBottom: 24 }}>
            {[...Array(5)].map((_, index) => (
              <Skeleton height={8 / 0.6} key={index} width="20%" />
            ))}
          </StyledFlex>

          {[...Array(3)].map((_, index) => (
            <StyledFlexBetweenH key={index}>
              <span>
                <Skeleton
                  height={12 / 0.6}
                  sx={{ display: 'inline-flex', marginRight: 1 }}
                  width={12}
                />
                <Skeleton
                  height={12 / 0.6}
                  sx={{ display: 'inline-flex' }}
                  width={150}
                />
              </span>
              <Skeleton height={12 / 0.6} width={50} />
            </StyledFlexBetweenH>
          ))}
        </>
      ) : (
        renderPopularProducts()
      )}
    </OverviewCard>
  );
}
